import {
  Article,
  ArticleAction,
  ArticleFooter,
  ArticleSubtitle,
  ArticleTitle,
} from "@babackapp/customer-ui/components/article";
import { Button } from "@babackapp/customer-ui/components/button";
import { Badge } from "@babackapp/ui/components/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@babackapp/customer-ui/components/tooltip";
import { getLineItemDisplay } from "@babackapp/return-widget-components/utils/line-item-display";
import { DiscountedPrice } from "@babackapp/return-widget-components/components/discounted-price";
import type {
  DraftLineItem,
  Order,
  OrderLineItem,
} from "@babackapp/return-widget-components/types";
import { InfoIcon, Trash2Icon } from "lucide-react";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { ExchangeArticleImage, getExchangeArticle } from "./exchange-article";

type Props = {
  item: DraftLineItem;
  orderLineItems: OrderLineItem[];
  order: Order | null;
  currency: string;
  isRetractation?: boolean;
  isConnectedUser?: boolean;
  connectedUserTooltip?: string;
  onRemove: () => void;
};

export function SummaryLineItem({
  item,
  orderLineItems,
  order,
  currency,
  isRetractation,
  isConnectedUser = false,
  connectedUserTooltip,
  onRemove,
}: Props) {
  const t = useCustomText();
  const display = getLineItemDisplay(orderLineItems, item.lineItemId);
  const { title, image, price, paidPrice, sku } = display;

  const { variantDisplay, exchangeTitle, exchangeImage, exchangeSku } =
    getExchangeArticle(order, item, display);
  // "A → B", "— → B" when the purchased article has no SKU, "A" alone.
  const skuDisplay = exchangeSku ? `${sku ?? "—"} → ${exchangeSku}` : sku;

  return (
    <Article>
      <ExchangeArticleImage
        src={image}
        exchangeSrc={exchangeImage}
        exchangeAlt={exchangeTitle ?? title}
        layoutId={`img-${item.lineItemId}`}
      />
      <ArticleTitle className="flex flex-wrap items-center gap-x-1.5">
        {title}
        {exchangeTitle && (
          <span className="font-normal text-muted-foreground">
            → {exchangeTitle}
          </span>
        )}
        {isRetractation && (
          <Badge variant="secondary" size="xs">
            {t("returnMethod.retractation.label")}
          </Badge>
        )}
      </ArticleTitle>
      <ArticleSubtitle>
        {[variantDisplay, `×${item.quantity}`].filter(Boolean).join(" — ")}
        {price != null && (
          <>
            {" — "}
            <DiscountedPrice
              catalogCents={price}
              paidCents={paidPrice}
              quantity={item.quantity}
              currency={currency}
            />
          </>
        )}
      </ArticleSubtitle>
      {isConnectedUser && skuDisplay && (
        <ArticleFooter className="flex items-center gap-1.5">
          <span>{skuDisplay}</span>
          <Tooltip>
            <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
              <InfoIcon className="size-3.5 cursor-help" />
            </TooltipTrigger>
            <TooltipContent>{connectedUserTooltip}</TooltipContent>
          </Tooltip>
        </ArticleFooter>
      )}
      <ArticleAction autoHide={false}>
        <Button variant="outline" size="icon-sm" onClick={onRemove}>
          <Trash2Icon />
        </Button>
      </ArticleAction>
    </Article>
  );
}
